import { AlertCircle, AlertTriangle, CheckCircle } from 'lucide-react'

type StatusAlertType = 'error' | 'success' | 'warning'

interface StatusAlertProps {
  type: StatusAlertType
  message: string
}

const STYLES: Record<
  StatusAlertType,
  { Icon: typeof AlertCircle; bg: string; text: string; icon: string }
> = {
  error: {
    Icon: AlertCircle,
    bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
    text: 'text-red-800 dark:text-red-200',
    icon: 'text-red-600 dark:text-red-400',
  },
  success: {
    Icon: CheckCircle,
    bg: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800',
    text: 'text-green-800 dark:text-green-200',
    icon: 'text-green-600 dark:text-green-400',
  },
  warning: {
    Icon: AlertTriangle,
    bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
    text: 'text-amber-800 dark:text-amber-200',
    icon: 'text-amber-600 dark:text-amber-400',
  },
}

// Errors interrupt, so a failed save is heard even though the alert is
// inserted already holding its text. Success is announced politely. Warnings
// get no role: several show on page load (e.g., a truncated-results notice),
// where an announcement would only interrupt.
const ROLES: Record<StatusAlertType, 'alert' | 'status' | undefined> = {
  error: 'alert',
  success: 'status',
  warning: undefined,
}

export function StatusAlert({ type, message }: StatusAlertProps) {
  // eslint-disable-next-line security/detect-object-injection
  const { Icon, bg, text, icon } = STYLES[type]
  // eslint-disable-next-line security/detect-object-injection
  const role = ROLES[type]

  return (
    <div role={role} className={`rounded-lg border p-4 ${bg}`}>
      <div className="flex items-start gap-3">
        <Icon aria-hidden="true" className={`w-5 h-5 flex-shrink-0 mt-0.5 ${icon}`} />
        <p className={`text-sm ${text}`}>{message}</p>
      </div>
    </div>
  )
}
